import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getDiwaliSeasonInfo, DIWALI_THEME_STORAGE_KEY } from '../utils/diwaliTheme';

export const GLOBAL_DIWALI_THEME_STORAGE_KEY = DIWALI_THEME_STORAGE_KEY;
export const GLOBAL_DIWALI_EVENT = 'niftysutra-global-diwali-changed';

export interface SystemThemeConfig {
  diwaliThemeEnabled?: boolean;
  updatedAt?: unknown;
  updatedBy?: string;
}

/**
 * Returns the cached or computed initial global Diwali theme state.
 */
export function getInitialGlobalDiwaliState(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const cached = localStorage.getItem(GLOBAL_DIWALI_THEME_STORAGE_KEY);
    if (cached === 'enabled') return true;
    if (cached === 'disabled') return false;
  } catch {
    // ignore localStorage access errors
  }

  // Default: automatic window 10 days before till 5 days after Diwali
  return getDiwaliSeasonInfo().isSeason;
}

/**
 * Subscribes to real-time global theme changes from Firestore.
 * When an admin enables or disables the Diwali theme, this callback is fired
 * on all connected users and synced in real time.
 */
export function subscribeToGlobalDiwaliTheme(
  callback: (enabled: boolean | null) => void
): () => void {
  const handleLocalEvent = (e: Event) => {
    const customEvent = e as CustomEvent<{ enabled: boolean }>;
    if (typeof customEvent.detail?.enabled === 'boolean') {
      callback(customEvent.detail.enabled);
    }
  };

  const handleStorage = (e: StorageEvent) => {
    if (e.key === GLOBAL_DIWALI_THEME_STORAGE_KEY) {
      if (e.newValue === 'enabled') callback(true);
      else if (e.newValue === 'disabled') callback(false);
      else callback(null);
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener(GLOBAL_DIWALI_EVENT, handleLocalEvent);
    window.addEventListener('storage', handleStorage);
  }

  if (!db || process.env.NODE_ENV === 'test') {
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(GLOBAL_DIWALI_EVENT, handleLocalEvent);
        window.removeEventListener('storage', handleStorage);
      }
    };
  }

  try {
    const docRef = doc(db, 'system_config', 'theme');
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as SystemThemeConfig;
          if (typeof data.diwaliThemeEnabled === 'boolean') {
            try {
              localStorage.setItem(
                GLOBAL_DIWALI_THEME_STORAGE_KEY,
                data.diwaliThemeEnabled ? 'enabled' : 'disabled'
              );
            } catch {
              // ignore
            }
            callback(data.diwaliThemeEnabled);
            return;
          }
        }
        // If not explicitly set in Firestore, use automated schedule
        callback(null);
      },
      (error) => {
        // Silently log warning on permission or network errors
        console.warn('Firestore global theme subscription warning:', error);
      }
    );

    return () => {
      unsubscribe();
      if (typeof window !== 'undefined') {
        window.removeEventListener(GLOBAL_DIWALI_EVENT, handleLocalEvent);
        window.removeEventListener('storage', handleStorage);
      }
    };
  } catch (err) {
    console.warn('Failed to attach Firestore global theme listener:', err);
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(GLOBAL_DIWALI_EVENT, handleLocalEvent);
        window.removeEventListener('storage', handleStorage);
      }
    };
  }
}

/**
 * Admin action: Sets the Diwali theme globally for ALL users.
 */
export async function setGlobalDiwaliTheme(
  enabled: boolean,
  adminUser?: { email?: string | null; uid?: string } | null
): Promise<void> {
  // 1. Update localStorage immediately for instantaneous UI responsiveness
  try {
    localStorage.setItem(GLOBAL_DIWALI_THEME_STORAGE_KEY, enabled ? 'enabled' : 'disabled');
  } catch {
    // ignore
  }

  // 2. Broadcast to other open tabs on this browser
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(GLOBAL_DIWALI_EVENT, { detail: { enabled } })
    );
  }

  // 3. Write to Firestore so it is pushed to all users worldwide in real time
  if (db && process.env.NODE_ENV !== 'test') {
    try {
      const docRef = doc(db, 'system_config', 'theme');
      await setDoc(
        docRef,
        {
          diwaliThemeEnabled: enabled,
          updatedAt: serverTimestamp(),
          updatedBy: adminUser?.email || adminUser?.uid || 'admin',
        },
        { merge: true }
      );
    } catch (err) {
      console.warn('Failed to set global Diwali theme in Firestore:', err);
    }
  }
}
